/**
 * Platform-agnostic Snapshot Module
 *
 * Exports snapshot functions for both Excel and Google Sheets.
 * Use the useSnapshot() hook in components to get the correct implementation.
 */

import { useMemo } from 'react'
import { usePlatform } from '../platform'
import {
  captureSnapshot as captureSnapshotExcel,
  restoreWorkbookSnapshot as restoreWorkbookSnapshotExcel,
} from './snapshot-excel'
import {
  captureSnapshot as captureSnapshotSheets,
  restoreWorkbookSnapshot as restoreWorkbookSnapshotSheets,
} from './snapshot-sheets'

// Re-export platform-agnostic types and storage functions
// These use IndexedDB and work on both platforms (async, non-blocking)
export type {
  CellFormat,
  CellSnapshot,
  SheetSnapshot,
  Snapshot,
  ToolDelta,
  TurnDeltaChain,
} from './snapshot-db'
export {
  associateTurnWithAfterSnapshot,
  associateTurnWithSnapshot,
  clearAllSnapshots,
  deleteDeltaChain,
  deleteSnapshot,
  getAfterSnapshotIdForTurn,
  getAllSnapshotIds,
  getDeltaChain,
  getSnapshot,
  getSnapshotForTurn,
  getSnapshotIdForTurn,
  getStorageInfo,
  saveDeltaChain,
  saveSnapshot,
} from './snapshot-db'

/**
 * Hook to get the correct snapshot implementation based on platform
 */
export function useSnapshot() {
  const platform = usePlatform()

  return useMemo(
    () => ({
      captureSnapshot:
        platform === 'excel' ? captureSnapshotExcel : captureSnapshotSheets,
      restoreWorkbookSnapshot:
        platform === 'excel'
          ? restoreWorkbookSnapshotExcel
          : restoreWorkbookSnapshotSheets,
    }),
    [platform]
  )
}
